import { Shop as ShopComponent } from '@shared/components/ui/shop';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { useTranslation } from 'react-i18next';
import { RefundOrder } from '../../models/RefundOrder';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

interface RefundCardProps {
  order: RefundOrder;
  selectedColumns?: string[];
}

export const RefundCard = ({ order, selectedColumns }: RefundCardProps) => {
  const { t } = useTranslation('refundsModule');
  const currency = useCurrency();

  const isVisible = (col: string) => !selectedColumns || selectedColumns.includes(col);

  const isPending = order.status === 'pending';

  return (
    <div className="px-4 py-3 border-b border-card-border">
      {/* Header: referencia + estado */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-title">{order.order_ref || '-'}</span>
        {isVisible('status') && (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium shrink-0 ${
              isPending ? 'bg-bg-warning text-warning' : 'bg-bg-info text-info'
            }`}
          >
            {t(`table.status.${order.status}`)}
          </span>
        )}
      </div>

      {/* Tienda */}
      {isVisible('shop_name') && order.shop_name && (
        <div className="mb-1">
          <ShopComponent name={order.shop_name} />
        </div>
      )}

      {/* Fecha + Importe + Suma liquidada — estilo boxes igual que stock en Products */}
      {(isVisible('delivered_at') || isVisible('total') || isVisible('sum_amount')) && (
        <div className="flex gap-2 mt-2">
          {isVisible('delivered_at') && (
            <div className="flex-1 bg-card-bg rounded py-1 text-center border border-card-border">
              <div className="text-xs mb-1">{t('table.columns.delivered_at')}</div>
              <div className="text-xs font-bold tabular-nums">{formatDate(order.delivered_at)}</div>
            </div>
          )}
          {isVisible('total') && (
            <div className="flex-1 bg-card-bg rounded py-1 text-center border border-card-border">
              <div className="text-xs mb-1">{t('table.columns.total')}</div>
              <div className="text-xs font-bold tabular-nums">
                {order.total != null ? formatCurrencyAmount(order.total, currency) : '-'}
              </div>
            </div>
          )}
          {isVisible('sum_amount') && (
            <div className="flex-1 bg-card-bg rounded py-1 text-center border border-card-border">
              <div className="text-xs mb-1">{t('table.columns.sum_amount')}</div>
              <div className="text-xs font-bold tabular-nums">
                {order.sum_amount != null ? formatCurrencyAmount(order.sum_amount, currency) : '-'}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
